import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Download, RefreshCw, CheckCircle2, Circle, Home, ExternalLink, Filter, MessageSquare } from "lucide-react";
import RequireAuth from "../components/RequireAuth";
import { authApi } from "../lib/auth";
import { toast } from "sonner";

const STATUS = { all: "All", pending: "Pending", contacted: "Contacted" };
const START_WHEN = ["Immediately", "Within 1 Month", "Within 3 Months", "Just Exploring"];

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function LeadsContent() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [when, setWhen] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [detail, setDetail] = useState(null); // lead for slide-out

  const load = async () => {
    setLoading(true);
    try {
      const r = await authApi.get("/admin/leads");
      setLeads(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
    const to = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;
    return leads.filter((l) => {
      if (status === "pending" && l.contacted) return false;
      if (status === "contacted" && !l.contacted) return false;
      if (when && l.start_when !== when) return false;
      if (from || to) {
        const t = l.created_at ? new Date(l.created_at).getTime() : 0;
        if (from && t < from) return false;
        if (to && t > to) return false;
      }
      if (!qq) return true;
      return [l.full_name, l.email, l.mobile, l.heard_from, (l.channels || []).join(",")]
        .join(" ").toLowerCase().includes(qq);
    });
  }, [leads, q, status, when, dateFrom, dateTo]);

  const stats = useMemo(() => ({
    total: leads.length,
    pending: leads.filter((l) => !l.contacted).length,
    contacted: leads.filter((l) => l.contacted).length,
    thisWeek: leads.filter((l) => {
      const t = l.created_at ? new Date(l.created_at).getTime() : 0;
      return t > Date.now() - 7 * 864e5;
    }).length,
  }), [leads]);

  const toggleContacted = async (lead) => {
    setSavingId(lead.lead_id);
    try {
      const next = !lead.contacted;
      await authApi.patch(`/admin/leads/${lead.lead_id}`, { contacted: next });
      setLeads((ls) => ls.map((l) => (l.lead_id === lead.lead_id ? { ...l, contacted: next } : l)));
      toast.success(next ? "Marked contacted" : "Marked pending");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const exportCsv = () => {
    const cols = [
      ["created_at", "Created At"],
      ["lead_id", "Lead ID"],
      ["full_name", "Full Name"],
      ["mobile", "Mobile"],
      ["email", "Email"],
      ["start_when", "Start When"],
      ["channels", "Channels"],
      ["has_website", "Website"],
      ["design_readiness", "Design Readiness"],
      ["sold_online_before", "Sold Before"],
      ["monthly_target", "Monthly Target"],
      ["heard_from", "Heard From"],
      ["contacted", "Contacted"],
      ["note", "Note"],
      ["calc.fabric", "Fabric"],
      ["calc.print_size", "Print Size"],
      ["calc.addons", "Add-ons"],
      ["calc.sell_price", "Sell Price"],
      ["calc.unit_cost", "Unit Cost"],
      ["calc.per_unit_profit", "Profit/pc"],
      ["utm.utm_source", "UTM Source"],
      ["utm.utm_campaign", "UTM Campaign"],
      ["ip", "IP"],
    ];
    const get = (row, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), row);
    const rows = filtered.map((l) => cols.map(([k]) => {
      const v = get(l, k);
      if (Array.isArray(v)) return v.join(", ");
      if (typeof v === "boolean") return v ? "Yes" : "No";
      if (k === "created_at" && v) return new Date(v).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
      return v ?? "";
    }));
    const csv = [cols.map(([, h]) => h).join(","), ...rows.map((r) => r.map(csvEscape).join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} lead(s)`);
  };

  const fmtDt = (d) => d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-950 text-white border-b-2 border-[#FFD400]">
        <div className="max-w-7xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" className="text-slate-300 hover:text-white"><Home className="w-4 h-4" /></Link>
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">/</span>
            <span className="font-extrabold uppercase text-sm">Leads</span>
          </div>
          <button onClick={load} disabled={loading} data-testid="leads-refresh" className="text-xs font-bold uppercase flex items-center gap-1.5 border border-white/30 px-3 py-1.5 hover:bg-white/10 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Total leads"   value={stats.total}   testid="stat-total" />
          <Stat label="Pending"       value={stats.pending} tone="amber" testid="stat-pending" />
          <Stat label="Contacted"     value={stats.contacted} tone="emerald" testid="stat-contacted" />
          <Stat label="Last 7 days"   value={stats.thisWeek} tone="indigo" testid="stat-week" />
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-slate-950 p-4 flex flex-wrap items-center gap-2" data-testid="leads-filters">
          <div className="relative grow min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="leads-search" placeholder="Search name, email, mobile…" className="w-full border-2 border-slate-950 pl-9 pr-3 py-2 text-sm" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-status" className="border-2 border-slate-950 px-2 py-2 text-xs font-bold uppercase">
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={when} onChange={(e) => setWhen(e.target.value)} data-testid="leads-when" className="border-2 border-slate-950 px-2 py-2 text-xs font-bold uppercase">
            <option value="">Any timing</option>
            {START_WHEN.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="leads-from" className="border-2 border-slate-950 px-2 py-2 text-xs" />
          <span className="text-xs text-slate-400">→</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="leads-to" className="border-2 border-slate-950 px-2 py-2 text-xs" />
          <div className="flex-1" />
          <button onClick={exportCsv} data-testid="leads-export" className="flex items-center gap-1.5 bg-slate-950 text-white text-xs font-bold uppercase px-4 py-2 border border-slate-950 hover:bg-slate-800">
            <Download className="w-3.5 h-3.5" /> Export CSV ({filtered.length})
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-slate-950 overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-950 text-white text-left text-xs uppercase tracking-widest">
              <tr>
                <th className="px-3 py-3 w-8"></th>
                <th className="px-3 py-3">Created</th>
                <th className="px-3 py-3">Name / Contact</th>
                <th className="px-3 py-3">Timing</th>
                <th className="px-3 py-3">Channels</th>
                <th className="px-3 py-3">Website</th>
                <th className="px-3 py-3">Designs</th>
                <th className="px-3 py-3">Target</th>
                <th className="px-3 py-3">Source</th>
                <th className="px-3 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading && (
                <tr><td colSpan="10" className="px-3 py-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="10" className="px-3 py-8 text-center text-slate-500">
                  {leads.length === 0 ? "No leads yet. Once /startpod visitors submit the form they'll appear here." : "No leads match the filters."}
                </td></tr>
              )}
              {!loading && filtered.map((l) => (
                <tr key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} className={l.contacted ? "bg-emerald-50/40" : ""}>
                  <td className="px-3 py-3">
                    <button onClick={() => toggleContacted(l)} disabled={savingId === l.lead_id} title={l.contacted ? "Mark pending" : "Mark contacted"} data-testid={`lead-contacted-${l.lead_id}`}>
                      {l.contacted ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-slate-300 hover:text-slate-500" />}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-xs font-mono text-slate-500 whitespace-nowrap">{fmtDt(l.created_at)}</td>
                  <td className="px-3 py-3">
                    <p className="font-bold text-slate-900">{l.full_name || "—"}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-2">
                      <a href={`https://wa.me/${(l.mobile || "").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline font-medium">{l.mobile}</a>
                      <span>·</span>
                      <a href={`mailto:${l.email}`} className="text-slate-700 hover:underline">{l.email}</a>
                    </p>
                  </td>
                  <td className="px-3 py-3 text-xs">{l.start_when || "—"}</td>
                  <td className="px-3 py-3 text-xs">{(l.channels || []).join(", ") || "—"}</td>
                  <td className="px-3 py-3 text-xs">{l.has_website || "—"}</td>
                  <td className="px-3 py-3 text-xs">{l.design_readiness || "—"}</td>
                  <td className="px-3 py-3 text-xs">{l.monthly_target || "—"}</td>
                  <td className="px-3 py-3 text-xs">{l.heard_from || "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => setDetail(l)} data-testid={`lead-view-${l.lead_id}`} className="text-slate-400 hover:text-slate-900"><ExternalLink className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* Detail slide-out */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 flex justify-end" data-testid="lead-detail" onClick={() => setDetail(null)}>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto border-l-2 border-slate-950" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-950 text-white px-5 py-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#FFD400]">Lead detail</p>
              <p className="text-lg font-extrabold">{detail.full_name || "—"}</p>
              <p className="text-xs font-mono text-slate-400 mt-1">{detail.lead_id} · {fmtDt(detail.created_at)}</p>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <KV k="Mobile" v={<a href={`https://wa.me/${(detail.mobile || "").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">{detail.mobile}</a>} />
              <KV k="Email"  v={<a href={`mailto:${detail.email}`} className="text-slate-800 hover:underline">{detail.email}</a>} />
              <KV k="Timing" v={detail.start_when} />
              <KV k="Channels" v={(detail.channels || []).join(", ")} />
              <KV k="Has website" v={detail.has_website} />
              <KV k="Design readiness" v={detail.design_readiness} />
              <KV k="Sold before" v={detail.sold_online_before} />
              <KV k="Monthly target" v={detail.monthly_target} />
              <KV k="Heard from" v={detail.heard_from} />
              {detail.calc && Object.keys(detail.calc).length > 0 && <>
                <p className="pt-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 border-t border-slate-200">Calculator context</p>
                <KV k="Fabric" v={detail.calc.fabric} />
                <KV k="Print size" v={detail.calc.print_size} />
                <KV k="Add-ons" v={(detail.calc.addons || []).join(", ")} />
                <KV k="Sell price" v={detail.calc.sell_price ? `₹${detail.calc.sell_price}` : "—"} />
                <KV k="Profit/pc" v={detail.calc.per_unit_profit != null ? `₹${detail.calc.per_unit_profit}` : "—"} />
              </>}
              {detail.utm && Object.keys(detail.utm).length > 0 && <>
                <p className="pt-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 border-t border-slate-200">UTM</p>
                {Object.entries(detail.utm).map(([k, v]) => <KV key={k} k={k} v={v} />)}
              </>}
              <p className="pt-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 border-t border-slate-200">Meta</p>
              <KV k="IP" v={detail.ip || "—"} />
              <KV k="UA" v={<span className="font-mono text-[11px] break-all">{detail.ua || "—"}</span>} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-950 text-white",
    amber: "bg-amber-500 text-slate-950",
    emerald: "bg-emerald-500 text-white",
    indigo: "bg-indigo-600 text-white",
  };
  return (
    <div className={`${tones[tone]} border-2 border-slate-950 p-4`} data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">{label}</p>
      <p className="text-3xl font-black font-mono mt-1">{value}</p>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 shrink-0">{k}</span>
      <span className="text-right text-slate-800">{v || "—"}</span>
    </div>
  );
}

export default function AdminLeads() {
  return <RequireAuth adminOnly redirectTarget="/admin/leads">{() => <LeadsContent />}</RequireAuth>;
}
